// src/utils/visitSummary.js
//
// Pure view-model logic for the patient's signed-visit screens. No React, no
// network — everything here is unit-tested (visitSummary.test.js).
//
// The server's VisitSummary (docs/visit-workflow-contract.md) is already
// patient-safe, but the screen still maps it through here so that:
//   - a section with nothing to show is hidden rather than rendered empty
//     (vitals=null, prescription=null, no next steps, no advice);
//   - vitals badges come ONLY from the server `flags` — the client never
//     recomputes a clinical threshold. A tile of a flag-bearing measure with
//     no flag reads "Normal"; a flag value we do not know is ignored;
//   - nothing clinical-coded reaches the page: the view-model is built field
//     by field (diagnosisCodes/doctorId are never copied) and a code that a
//     doctor typed into the diagnosis text, e.g. "Hypertension (I10)", is
//     stripped.

import { isLabTestSuggestion, LAB_TEST_SUGGESTION_PREFIX } from './labTestSuggestion';

/** A visit signed this recently counts as "new" on the Today screen. */
export const NEW_VISIT_WINDOW_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── dates ───────────────────────────────────────────────────────────────────

/** Parses an API date or local date-time. A bare YYYY-MM-DD is local midnight
 *  (new Date('YYYY-MM-DD') would be UTC and can land on the previous day). */
export function parseApiDate(value) {
  if (!value || typeof value !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "28 Oct 2026" — fixed format so output does not depend on the runtime locale. */
export function formatVisitDate(value) {
  const d = parseApiDate(value);
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : '';
}

/** True when the visit is within the last 7 days (the boundary itself counts). */
export function isRecentVisit(value, now = new Date(), windowDays = NEW_VISIT_WINDOW_DAYS) {
  const d = parseApiDate(value);
  if (!d) return false;
  return now.getTime() - d.getTime() <= windowDays * DAY_MS;
}

// ── text ────────────────────────────────────────────────────────────────────

// ICD-10-shaped code: a letter, two digits, optional ".xx" — "I10", "E78.0".
const PAREN_CODE = /\s*[([]\s*[A-TV-Z]\d{2}(?:\.\w{1,4})?\s*[)\]]/g;
const LEADING_CODE = /^\s*[A-TV-Z]\d{2}(?:\.\w{1,4})?\s*[-–:]\s+/;

/** Removes diagnosis codes a doctor may have typed into free text. */
export function stripClinicalCodes(text) {
  if (typeof text !== 'string') return '';
  return text.replace(PAREN_CODE, '').replace(LEADING_CODE, '').replace(/\s{2,}/g, ' ').trim();
}

function cleanText(text) {
  return typeof text === 'string' && text.trim() ? text.trim() : null;
}

/** "Dr. Priya Nair" whether or not the server already prefixed it. */
export function doctorLabel(name) {
  const n = cleanText(name);
  if (!n) return 'your doctor';
  return /^dr\.?(\s|$)/i.test(n) ? n : `Dr. ${n}`;
}

// ── vitals ──────────────────────────────────────────────────────────────────

// Server flag -> badge. Unknown flags are not in this table and are ignored.
// tone: 'alert' (needs a look) or 'watch' (lower than usual); the page maps
// them to badge classes. Thresholds live on the server, never here.
const FLAG_BADGES = {
  BP_HIGH:      { label: 'High', tone: 'alert' },
  BP_LOW:       { label: 'Low',  tone: 'watch' },
  SPO2_LOW:     { label: 'Low',  tone: 'alert' },
  FEVER:        { label: 'High', tone: 'alert' },
  TACHYCARDIA:  { label: 'High', tone: 'watch' },
  BRADYCARDIA:  { label: 'Low',  tone: 'watch' },
};
const NORMAL_BADGE = { label: 'Normal', tone: 'ok' };

/** Badge for one tile from the flags that apply to it; null = no badge at all. */
export function badgeForFlags(flags, applicable) {
  const list = Array.isArray(flags) ? flags : [];
  const hit = applicable.find(f => list.includes(f));
  return hit ? FLAG_BADGES[hit] : NORMAL_BADGE;
}

const has = v => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

export function buildVitalsModel(v) {
  if (!v || typeof v !== 'object') return null;
  const tiles = [];
  const add = (key, label, value, unit, applicable) => tiles.push({
    key, label, value: String(value), unit,
    badge: applicable ? badgeForFlags(v.flags, applicable) : null,
  });

  if (has(v.systolic) && has(v.diastolic)) {
    add('bp', 'Blood pressure', `${v.systolic}/${v.diastolic}`, 'mmHg', ['BP_HIGH', 'BP_LOW']);
  }
  if (has(v.pulse))        add('pulse', 'Pulse', v.pulse, 'bpm', ['TACHYCARDIA', 'BRADYCARDIA']);
  if (has(v.spo2))         add('spo2', 'Oxygen level', v.spo2, '%', ['SPO2_LOW']);
  if (has(v.temperatureC)) add('temp', 'Temperature', v.temperatureC, '°C', ['FEVER']);
  if (has(v.weightKg))     add('weight', 'Weight', v.weightKg, 'kg', null);
  if (has(v.heightCm))     add('height', 'Height', v.heightCm, 'cm', null);
  if (has(v.bmi))          add('bmi', 'BMI', v.bmi, '', null);
  if (has(v.waistCm))      add('waist', 'Waist', v.waistCm, 'cm', null);

  if (!tiles.length) return null;
  return {
    measuredOn: formatVisitDate(v.measuredAt),
    tiles,
    anyFlagged: tiles.some(t => t.badge && t.badge.tone !== 'ok'),
  };
}

// ── medicines ───────────────────────────────────────────────────────────────

export function buildPrescriptionModel(rx) {
  if (!rx || typeof rx !== 'object') return null;
  const items = (Array.isArray(rx.items) ? rx.items : [])
    .filter(i => i && cleanText(i.drugName))
    .map(i => {
      const strength = cleanText(i.strength);
      return {
        name: strength ? `${i.drugName.trim()} ${strength}` : i.drugName.trim(),
        dose: cleanText(i.dose),
        timing: cleanText(i.plainTiming),
        duration: has(i.durationDays)
          ? `for ${i.durationDays} day${Number(i.durationDays) === 1 ? '' : 's'}`
          : null,
        instructions: cleanText(i.instructions),
      };
    });
  if (!items.length) return null;
  return {
    id: rx.id || null,
    rxNumber: cleanText(rx.rxNumber),
    items,
  };
}

// ── next steps ──────────────────────────────────────────────────────────────

// The signed visit adds "Book follow-up appointment (in N weeks)" as a step.
// The page shows follow-up once, as its own row, so that step is folded in.
const FOLLOW_UP_STEP = /^book follow-up appointment/i;

export function isLabSuggestionStep(description) {
  return isLabTestSuggestion(description);
}

export function buildNextSteps(steps, followUpWeeks) {
  const weeks = has(followUpWeeks) && Number(followUpWeeks) > 0 ? Number(followUpWeeks) : null;
  const out = [];
  (Array.isArray(steps) ? steps : []).forEach(s => {
    const raw = s && cleanText(s.description);
    if (!raw) return;
    const isFollowUp = FOLLOW_UP_STEP.test(raw);
    if (isFollowUp && weeks) return; // shown as the follow-up row
    const isLabs = isLabSuggestionStep(raw);
    out.push({
      id: s.id ?? raw,
      kind: isLabs ? 'labs' : isFollowUp ? 'followup' : 'task',
      text: isLabs ? `Tests suggested:${raw.slice(LAB_TEST_SUGGESTION_PREFIX.length)}` : raw,
      dueOn: formatVisitDate(s.dueDate),
      completed: Boolean(s.completed),
    });
  });
  return {
    steps: out,
    followUp: weeks ? { weeks, label: `Review visit in ${weeks} week${weeks === 1 ? '' : 's'}` } : null,
  };
}

// ── whole summary ───────────────────────────────────────────────────────────

export function buildVisitSummaryModel(summary) {
  if (!summary || typeof summary !== 'object') return null;
  const vitals = buildVitalsModel(summary.vitals);
  const prescription = buildPrescriptionModel(summary.prescription);
  const { steps, followUp } = buildNextSteps(summary.nextSteps, summary.followUpWeeks);
  const diagnosis = cleanText(stripClinicalCodes(summary.diagnosis));
  const advice = cleanText(summary.adviceForPatient);
  const allergies = cleanText(summary.allergies);

  return {
    id: summary.visitId,
    dateLabel: formatVisitDate(summary.date),
    doctor: doctorLabel(summary.doctorName),
    specialty: cleanText(summary.doctorSpecialty),
    clinic: cleanText(summary.clinicName),
    diagnosis,
    advice,
    vitals,
    prescription,
    // Shown with the medicines only; no medicines, no allergy warning.
    allergyWarning: prescription && allergies ? allergies : null,
    steps,
    followUp,
    show: {
      diagnosis: Boolean(diagnosis),
      vitals: Boolean(vitals),
      prescription: Boolean(prescription),
      nextSteps: steps.length > 0 || Boolean(followUp),
      advice: Boolean(advice),
    },
  };
}

// ── list + "new visit" ──────────────────────────────────────────────────────

export function buildVisitListModel(items) {
  return (Array.isArray(items) ? items : [])
    .filter(i => i && i.visitId)
    .map(i => ({
      id: i.visitId,
      date: i.date,
      dateLabel: formatVisitDate(i.date),
      doctor: doctorLabel(i.doctorName),
      headline: cleanText(stripClinicalCodes(i.diagnosisHeadline)),
    }))
    .sort((a, b) => (parseApiDate(b.date)?.getTime() ?? 0) - (parseApiDate(a.date)?.getTime() ?? 0));
}

/** Newest visit that is within 7 days and not yet opened, or null. */
export function findNewVisit(items, seenIds = [], now = new Date()) {
  const seen = new Set(seenIds);
  return buildVisitListModel(items).find(v => isRecentVisit(v.date, now) && !seen.has(v.id)) || null;
}

// "Opened" is a per-device convenience only, so every access is guarded:
// storage can be blocked or full, and the Today card must still render.
const seenKey = patientId => `previu_seen_visits_${patientId}`;

export function readSeenVisitIds(patientId) {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(seenKey(patientId)) || '[]');
    return Array.isArray(parsed) ? parsed.filter(x => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function markVisitSeen(patientId, visitId) {
  try {
    const ids = readSeenVisitIds(patientId);
    if (!ids.includes(visitId)) {
      window.localStorage.setItem(seenKey(patientId), JSON.stringify([...ids, visitId].slice(-50)));
    }
  } catch {
    /* storage unavailable — the card may show again; harmless */
  }
}
